import Button from "@mui/material/Button";
import React, { useState, useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { FormikHelpers } from "formik";

import { Tray } from "../models/tray";
import { traysApi } from "../services/api/trays";
import { goBackOrTo } from "../utils/history";
import { ErrorContext } from "../components/app-routes";
import Page from "../components/page";
import TrayForm, { TrayFormValues } from "../components/tray-form";
import LoadingBackdrop from "../components/loading-backdrop";
import StickyActionBar from "../components/sticky-action-bar";
import { Unit } from "../models/unit";
import { unitsApi } from "../services/api/units";

export default function TrayPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [tray, setTray] = useState<Tray | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [units, setUnits] = useState<Unit[] | null>(null);

  const getUnits = async () => {
    try {
      const units = await unitsApi.getAll();
      setUnits(units);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    getUnits();
  }, []);

  useEffect(() => {
    async function getTray() {
      try {
        const trays = await traysApi.getAll();
        const tray = trays.find((v) => v.id === Number(id));

        if (!tray) {
          throw new Error("Invalid state");
        }

        setTray(tray);
      } catch (error) {
        handleError(error);
      }
    }

    getTray();
  }, [id, handleError]);

  const onSubmit = async (
    values: TrayFormValues,
    _helpers: FormikHelpers<TrayFormValues>,
  ) => {
    setBusy(true);

    try {
      await traysApi.update({
        id: values.id,
        name: values.name,
        soilPerTray:
          values.soilPerTray === "" ? null : Number(values.soilPerTray),
        unit: values.unit,
        notes: values.notes,
        archivedAt: values.archived ? DateTime.now().toISO() : null,
      });

      mixpanel.track("Update Tray");

      setSaved(true);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (saved) {
      history.replace("/home/trays");
    }
  }, [saved, history]);

  return (
    <Page title="Tray">
      {tray && units && (
        <TrayForm
          initialValues={{
            id: tray.id,
            name: tray.name,
            soilPerTray:
              tray.soilPerTray === null ? "" : tray.soilPerTray.toString(),
            unit: tray.unit,
            notes: tray.notes,
            archived: tray.archivedAt !== null,
          }}
          unitOptions={(units ?? []).filter((unit) => unit.archivedAt === null)}
          onUnitsChanged={getUnits}
          onSubmit={onSubmit}
          saved={saved}
        >
          <StickyActionBar position="bottom">
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => goBackOrTo(history, "/home/trays")}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={busy}
            >
              Save
            </Button>
          </StickyActionBar>
        </TrayForm>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
